import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import useAuth from '../context/AuthProvider';
import { AppRoutes } from '../helper/appRoutes';
import { CLIENT_HOME_ROLES, hasAnyRole } from '../helper/userRoles';
import { apiClient } from '../helper/api';
import {
  EstimatorGridItem,
  getBuiltInStateConfig,
  getEstimatorGridItems,
  getEstimatorStateKind,
  getExternalStateConfig,
  mergeEstimatorGridWithStateIds,
  SURPLUS_LINES_TAX_ESTIMATOR_TERMS_TEXT,
  SURPLUS_LINES_TAX_ESTIMATOR_TERMS_URL,
  SURPLUS_LINES_TAX_ESTIMATOR_TERMS_VERSION,
} from '../helper/surplusLinesTaxEstimatorConfig';
import { SL_ESTIMATOR_BLUE } from '../helper/surplusLinesTaxEstimatorStyles';
import { TaxCalculatorResult } from '../helper/surplusLinesTaxCalculator';
import { logTaxEstimatorUsage } from '../services/api/taxEstimatorService';
import {
  BG_SUBTLE,
  BORDER,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../components/Elements/AdminHomeDashboardTile';
import { SurplusLinesTaxEstimatorForm } from '../components/Elements/SurplusLinesTaxEstimatorForm';
import { SurplusLinesTaxEstimatorStateCard } from '../components/Elements/SurplusLinesTaxEstimatorStateCard';

type ApiStateOption = {
  stateId: number;
  stateName: string;
  postalAbbv: string;
};

export const SurplusLinesTaxEstimator: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsWarning, setTermsWarning] = useState(false);
  const [apiStates, setApiStates] = useState<ApiStateOption[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [statesError, setStatesError] = useState<string | null>(null);

  const gridItems = useMemo(
    () => mergeEstimatorGridWithStateIds(getEstimatorGridItems(), apiStates),
    [apiStates],
  );

  const homeRoute = hasAnyRole(user?.userRoles, CLIENT_HOME_ROLES)
    ? AppRoutes.client.home
    : AppRoutes.home.base;

  const stateKind = selectedState ? getEstimatorStateKind(selectedState) : null;
  const externalConfig = selectedState ? getExternalStateConfig(selectedState) : undefined;
  const builtInConfig = selectedState ? getBuiltInStateConfig(selectedState) : undefined;

  useEffect(() => {
    const token = user?.token;
    if (!token) {
      setApiStates([]);
      setStatesError('Unable to load states.');
      setStatesLoading(false);
      return;
    }

    let cancelled = false;

    const fetchStates = async () => {
      setStatesLoading(true);
      setStatesError(null);
      try {
        const responseJson = await apiClient('/States/getStates', {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        });

        if (cancelled) return;

        if (responseJson?.status && Array.isArray(responseJson.data) && responseJson.data.length > 0) {
          setApiStates(responseJson.data as ApiStateOption[]);
        } else {
          setApiStates([]);
          setStatesError('Unable to load states.');
        }
      } catch {
        if (!cancelled) {
          setApiStates([]);
          setStatesError('Unable to load states.');
        }
      } finally {
        if (!cancelled) {
          setStatesLoading(false);
        }
      }
    };

    fetchStates();

    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  const logUsage = useCallback(
    async (
      stateId: number,
      eventType: 'calculate' | 'external_link',
      payload: {
        premiumAmount?: number | null;
        feesAmount?: number | null;
        policyEffectiveDate?: string | null;
        endorsementEffectiveDate?: string | null;
        transactionTypeId?: number | null;
        calculatedResultsJson?: object | null;
      },
    ) => {
      if (!user?.token || !stateId) return;

      await logTaxEstimatorUsage(user.token, {
        stateId,
        eventType,
        ...payload,
        termsAcceptedVersion: SURPLUS_LINES_TAX_ESTIMATOR_TERMS_VERSION,
      });
    },
    [user?.token],
  );

  const handleGridClick = async (item: EstimatorGridItem) => {
    if (!termsAccepted) {
      setTermsWarning(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!item.stateId) {
      setTermsWarning(false);
      setStatesError(`Unable to resolve state id for ${item.stateName}.`);
      return;
    }

    setTermsWarning(false);
    setStatesError(null);
    const kind = getEstimatorStateKind(item.stateName);

    if (kind === 'external') {
      const external = getExternalStateConfig(item.stateName);
      if (!external) return;
      window.open(external.externalUrl, '_blank', 'noopener,noreferrer');
      await logUsage(item.stateId, 'external_link', {});
      return;
    }

    if (kind === 'builtIn') {
      setSelectedState(item.stateName);
      setSelectedStateId(item.stateId);
    }
  };

  const handleCalculate = async (
    result: TaxCalculatorResult,
    inputs: {
      premium: number;
      fees: number;
      policyEffectiveDate: string;
      endorsementEffectiveDate: string;
      transactionTypeId: number;
      transactionTypeName: string;
    },
  ) => {
    if (!selectedState || !selectedStateId) return;
    await logUsage(selectedStateId, 'calculate', {
      premiumAmount: inputs.premium,
      feesAmount: inputs.fees,
      transactionTypeId: inputs.transactionTypeId,
      policyEffectiveDate: inputs.policyEffectiveDate || null,
      endorsementEffectiveDate: inputs.endorsementEffectiveDate || null,
      calculatedResultsJson: {
        stateId: selectedStateId,
        stateName: selectedState,
        transactionTypeId: inputs.transactionTypeId,
        transactionTypeName: inputs.transactionTypeName,
        lines: result.lines,
        total: result.total,
        taxablePremium: result.taxablePremium,
        grandTotal: result.grandTotal,
      },
    });
  };

  const handleBackToGrid = () => {
    setSelectedState(null);
    setSelectedStateId(null);
  };

  const termsSection = (
    <Box
      sx={{
        mb: 3,
        pb: 2,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              if (e.target.checked) setTermsWarning(false);
            }}
            sx={{ color: SL_ESTIMATOR_BLUE, '&.Mui-checked': { color: SL_ESTIMATOR_BLUE } }}
          />
        }
        label={
          <Typography sx={{ fontSize: '0.875rem', color: TEXT_SECONDARY }}>
            I agree to the{' '}
            <Link
              href={SURPLUS_LINES_TAX_ESTIMATOR_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              sx={{ color: SL_ESTIMATOR_BLUE, fontWeight: 600 }}
            >
              Terms &amp; Conditions
            </Link>
            . {SURPLUS_LINES_TAX_ESTIMATOR_TERMS_TEXT}
          </Typography>
        }
      />
      {termsWarning && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Accept the Terms &amp; Conditions before selecting a state.
        </Alert>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: { xs: 'calc(100vh - 85px)', md: 'calc(100vh - 250px)' },
        backgroundColor: BG_SUBTLE,
        marginLeft: { xs: '-24px', md: '0px' },
        marginRight: { xs: '-24px', md: '0px' },
        paddingBottom: 3,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (selectedState ? handleBackToGrid() : navigate(homeRoute))}
          sx={{ mb: 2, color: SL_ESTIMATOR_BLUE }}
        >
          {selectedState ? 'Back to States' : 'Back to Home'}
        </Button>

        {!selectedState && (
          <>
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                fontWeight: 700,
                color: TEXT_PRIMARY,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                mb: 2,
              }}
            >
              Surplus Lines Tax Estimators
            </Typography>

            {termsSection}

            {statesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {statesError}
              </Alert>
            )}

            {statesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: SL_ESTIMATOR_BLUE }} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 2,
                  mb: 3,
                }}
              >
                {gridItems.map((item) => (
                  <SurplusLinesTaxEstimatorStateCard
                    key={item.stateCode}
                    stateName={item.stateName}
                    subtitle={item.gridSubtitle}
                    onClick={() => handleGridClick(item)}
                  />
                ))}
              </Box>
            )}
          </>
        )}

        {selectedState && stateKind === 'builtIn' && builtInConfig && (
          <SurplusLinesTaxEstimatorForm
            config={builtInConfig}
            termsAccepted={termsAccepted}
            onCalculate={handleCalculate}
          />
        )}

        {selectedState && stateKind === 'external' && externalConfig && (
          <Box sx={{ maxWidth: '640px', mx: 'auto' }}>
            <Alert severity="info">
              {externalConfig.stateName} uses an external calculator ({externalConfig.linkLabel}).
              Return to the state grid to open it.
            </Alert>
          </Box>
        )}

      </Container>
    </Box>
  );
};

export default SurplusLinesTaxEstimator;
